import { useCallback, useMemo, useState } from 'react'

import { useEventListener } from '@hozana/hooks/useEventListener'
import { useTimeout } from '@hozana/hooks/useTimeout'
import { useScreen } from '@hozana/screen/useScreen'
import { cookie } from '@hozana/storage/cookies'

import { COOKIES, COOKIES_CONFIG } from 'general/managers/cookies/constants'
import { POPINS } from 'general/managers/popins/constants'
import { PromptType } from 'general/managers/prompts/constants'
import { usePromptsManagement } from 'general/managers/prompts/usePromptsManagement'

import { getDisplayCommunityPropositionsPopin, getSubscribeOffer, getUserRole } from 'modules/community/functions'

import type { TCommunity } from '../types'

const SCROLL_UP_DISTANCE = 100
const STARTUP_DELAY = 10000
const POP_DELAY = 60000

export type TCommunitiesPropositionsProps = {
  community: TCommunity
  publicationId?: number
  disabled?: boolean
}

/**
 * ### useCommunitiesPropositions ###
 *
 * Manage CommunitiesPropositionsPopin.
 * Show the popin if the conditions are ok:
 * - {POP_DELAY}ms after mount
 * - Or after {STARTUP_DELAY} when:
 *   - the mouse goes over the top of the page on desktop
 *   - the user scrolls up more than {SCROLL_UP_DISTANCE}.
 */
export const useCommunitiesPropositions = ({ community }: TCommunitiesPropositionsProps) => {
  const screen = useScreen()
  const isSmallScreen = screen.xs || screen.sm

  const poppedCommunities = useMemo(() => cookie.load<number[]>(COOKIES.poppedCommunities) || [], [])

  // Wether the popin can be shown or not
  const [show, setShow] = useState(false)
  const [delay, setDelay] = useState(POP_DELAY)

  const [scrollY, setScrollY] = useState(0)
  const [scrollUpY, setScrollUpY] = useState(0)

  /** True if the subscription to the community can be suggested */
  const subscribeOffer = useMemo(() => getSubscribeOffer(community), [community])

  const onPop = useCallback(
    () => cookie.save(COOKIES.poppedCommunities, [...poppedCommunities, community?.id], COOKIES_CONFIG.SESSION),
    [community?.id, poppedCommunities],
  )

  /* SET SHOW TO TRUE */

  /** Set show to true if the conditions are ok */
  const setShowToTrue = useCallback(() => {
    const { isCreator, isAdmin, isAnimator } = getUserRole(community)

    const alreadyPopped = poppedCommunities.includes(community?.id)

    if (
      (subscribeOffer || community?.communitiesPropositions) &&
      !alreadyPopped &&
      !isCreator &&
      !isAdmin &&
      !isAnimator &&
      getDisplayCommunityPropositionsPopin(community)
    ) {
      setShow(true)
    }
  }, [community, poppedCommunities, subscribeOffer])

  // Call setShowToTrue after {STARTUP_DELAY}
  useTimeout(setShowToTrue, STARTUP_DELAY)

  /* SHOW TIMING */

  /** Remove the delay and show the popin */
  const showPopinNow = useCallback(() => setDelay(undefined), [])

  const eventListener: Parameters<typeof useEventListener> = useMemo(() => {
    const handleScroll = () => {
      if (show) {
        const newScrollY = global.scrollY
        if (scrollY && newScrollY <= scrollY) {
          if (!scrollUpY && newScrollY > SCROLL_UP_DISTANCE) {
            setScrollUpY(newScrollY)
          } else if (scrollUpY && newScrollY < scrollUpY - SCROLL_UP_DISTANCE) {
            showPopinNow()
          }
        } else {
          setScrollUpY(0)
        }
        setScrollY(newScrollY)
      }
    }

    if (isSmallScreen) {
      return ['scroll', handleScroll, undefined, !show]
    }
    return ['mouseleave', showPopinNow, typeof document !== 'undefined' ? document.documentElement : undefined, !show]
  }, [isSmallScreen, showPopinNow, show, scrollY, scrollUpY])

  useEventListener(...eventListener)

  /* PROMPTS MANAGEMENT */

  const popinOptions = useMemo(() => ({ community, subscribeOffer }), [community, subscribeOffer])

  usePromptsManagement({
    type: PromptType.COMMUNITIES_PROPOSITIONS,
    show,
    delay,
    popinName: POPINS.CommunitiesPropositionsPopin,
    popinOptions,
    onPop,
  })
}
